import { CmsPage } from "../types";

export const defaultCmsPages: CmsPage[] = [
  {
    id: "terms",
    title: "Terms and Conditions",
    content: `<h1>Terms and Conditions</h1>
<p class="updated">Last updated: August 25, 2026</p>

<p>These Terms and Conditions ("Terms") govern your use of the RentLow website and mobile application (the "Service"), operated by RentLow ("we", "us", "our"). By creating an account, placing an order, or otherwise using the Service, you agree to be bound by these Terms. If you do not agree, please do not use the Service.</p>

<h2>1. Eligibility</h2>
<p>You must be at least 18 years old and capable of entering into a legally binding agreement to use the Service. By using RentLow, you confirm that you meet this requirement.</p>

<h2>2. Rental Orders</h2>
<ul>
  <li>All products listed on RentLow are available strictly on a rental basis for the duration selected at checkout.</li>
  <li>Orders are confirmed only after successful payment or deposit, as applicable.</li>
  <li>Delivery and pickup timelines are estimates and may vary based on location and availability.</li>
  <li>Products remain the property of RentLow at all times and must be returned in the condition they were delivered, subject to normal wear and tear.</li>
</ul>

<h2>3. Payments and Invoicing</h2>
<ul>
  <li>Rental charges, security deposits, and any applicable fees are displayed at the time of booking.</li>
  <li>Invoices are generated automatically through our platform and made available to you for each billing cycle.</li>
  <li>Late payments may result in additional charges, suspension of the rental, or recovery of the product.</li>
</ul>

<h2>4. Damage, Loss, and Security Deposit</h2>
<ul>
  <li>You are responsible for the product during the rental period and must take reasonable care of it.</li>
  <li>Damage beyond normal wear and tear, or loss of the product, may be deducted from your security deposit or billed separately.</li>
  <li>The security deposit (where applicable) will be refunded after the product is returned and inspected, minus any applicable deductions.</li>
</ul>

<h2>5. Cancellations and Returns</h2>
<ul>
  <li>Cancellation requests must be made before the scheduled delivery to be eligible for a full refund, subject to our cancellation policy at the time of booking.</li>
  <li>Early termination of an ongoing rental may be subject to minimum lock-in charges as communicated during checkout.</li>
</ul>

<h2>6. User Responsibilities</h2>
<ul>
  <li>You agree to provide accurate personal, contact, and address information.</li>
  <li>You agree not to sublet, resell, or transfer rented products to any third party without our written consent.</li>
  <li>You agree to use the Service only for lawful purposes.</li>
</ul>

<h2>7. Limitation of Liability</h2>
<p>RentLow is not liable for indirect, incidental, or consequential damages arising from your use of the Service, including delays in delivery or product malfunction, to the extent permitted by applicable law.</p>

<h2>8. Termination</h2>
<p>We reserve the right to suspend or terminate your account for violation of these Terms, non-payment, or misuse of the Service.</p>

<h2>9. Changes to These Terms</h2>
<p>We may update these Terms from time to time. Continued use of the Service after changes are posted constitutes acceptance of the revised Terms.</p>

<h2>10. Governing Law</h2>
<p>These Terms are governed by the laws of India, and any disputes shall be subject to the jurisdiction of the courts in Bhopal, Madhya Pradesh.</p>

<h2>11. Contact Us</h2>
<p>For questions about these Terms, contact us at:</p>
<p>Email: <a href="mailto:support@rentlow.co.in">rentlowin@gmail.com</a><br>
Website: <a href="https://rentlow.co.in">https://rentlow.co.in</a></p>`,
    updatedAt: "2026-08-25T00:00:00.000Z"
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    content: `<h1>Privacy Policy</h1>
<p class="updated">Last updated: August 25, 2026</p>
<h2>1. Information We Collect</h2>
<p>We respect your privacy. RentLow only requests the minimum customer information needed to fulfill secure delivery and order management: customer name, verified mobile phone number, delivery address, nearby landmark, and selected pin code.</p>
<h2>2. Data Protection & Security</h2>
<p>All sensitive information is securely stored in our Google Cloud-hosted databases. We strictly do not share, lease, sell, or disclose your location, contact number, or rental history with unauthorized third-party marketing services or advertising networks.</p>
<h2>3. Purpose of Processing</h2>
<p>We use your information solely to process rental bookings, coordinate 24-hour express doorstep delivery and setup, manage refundable escrow security deposits, and provide post-delivery customer support.</p>
<h2>4. Contact Us</h2>
<p>For privacy-related inquiries, reach us at <a href="mailto:support@rentlow.co.in">rentlowin@gmail.com</a> or via our official website <a href="https://rentlow.co.in">https://rentlow.co.in</a>.</p>`,
    updatedAt: "2026-08-25T00:00:00.000Z"
  },
  {
    id: "payment",
    title: "Payment Terms",
    content: `<h1>Payment Terms</h1>
<p class="updated">Last updated: August 25, 2026</p>
<h2>1. Zero Advance Online Payment Required</h2>
<p>To ensure maximum safety and user confidence, RentLow does not require any online credit card or direct net-banking prepayments on our website. All checkout orders are registered with payment due on delivery.</p>
<h2>2. Payment on Doorstep Delivery</h2>
<p>Customers settle the initial refundable security deposit and first month's rental fee in person upon delivery and inspection. Payment can be made seamlessly via Cash or any popular UPI apps (Google Pay, PhonePe, Paytm, BHIM).</p>
<h2>3. Invoicing and Subsequent Billing</h2>
<p>Digital invoices are generated and accessible through your account and SMS notifications for each subscription cycle.</p>`,
    updatedAt: "2026-08-25T00:00:00.000Z"
  },
  {
    id: "refund",
    title: "Refund & Escrow Terms",
    content: `<h1>Refund & Escrow Terms</h1>
<p class="updated">Last updated: August 25, 2026</p>
<h2>1. Security Deposit Refund Guarantee</h2>
<p>Your refundable security deposit is safeguarded in a dedicated escrow ledger. Upon completion of your rental subscription, our technician will conduct a standard physical inspection.</p>
<h2>2. Instant Doorstep Settlement</h2>
<p>Once the appliance is retrieved in normal working condition, the security deposit is refunded 100% instantly on the spot via UPI or cash directly to you without processing delays or hidden deductions.</p>`,
    updatedAt: "2026-08-25T00:00:00.000Z"
  },
  {
    id: "return-policy",
    title: "Return & Replacement Policy",
    content: `<h1>Return, Replacement & Cancellation Policy</h1>
<p class="updated">Last updated: October 23, 2026 | Effective for Bhopal & Sehore</p>

<p>At RentLow, customer trust and satisfaction are our highest priorities. We provide an unconditional doorstep return guarantee and comprehensive repair or exchange coverage across all rented appliances and furniture.</p>

<h2>1. Immediate Return at the Time of Delivery (Zero Risk Guarantee)</h2>
<p>We want you to be completely satisfied with what you receive. If upon delivery:</p>
<ul>
  <li>The delivered product is not as committed by our sales or customer care team;</li>
  <li>The product does not match the specifications, model, or condition promised;</li>
  <li>The product does not fit the intended room layout or entryway; or</li>
  <li>You are dissatisfied with the physical appearance or initial test-run;</li>
</ul>
<p><strong>You are fully entitled to reject and return the product immediately on the spot with our delivery executive.</strong></p>
<ul>
  <li><strong>₹0 Payment:</strong> Because RentLow charges rental fees and deposits only after installation and your physical inspection, you pay absolutely ₹0 if you decline the delivery.</li>
  <li><strong>No Penalties:</strong> There are zero cancellation charges, zero restocking fees, and zero delivery penalties for immediate doorstep returns.</li>
</ul>

<h2>2. Free Doorstep Repair Guarantee for Faulty Products</h2>
<p>If any appliance or furniture develops an operational fault, mechanical glitch, cooling defect, heating issue, or technical breakdown during your rental subscription:</p>
<ul>
  <li>Our verified service engineers attend to your residence within <strong>24 to 48 hours</strong> across Bhopal and Sehore.</li>
  <li><strong>100% Free Coverage:</strong> You incur zero service charges, zero technician visit fees, and zero cost for standard functional repair components.</li>
  <li>Maintenance is completely complimentary throughout your active tenure.</li>
</ul>

<h2>3. Instant Product Exchange & Replacement</h2>
<p>In the event that an item suffers a technical defect that cannot be resolved quickly on-site:</p>
<ul>
  <li><strong>Immediate Exchange:</strong> RentLow will exchange and replace the faulty unit with an identical or superior mint-condition unit at <strong>zero additional cost</strong>.</li>
  <li><strong>Seamless Swap:</strong> Our logistics crew safely collects the faulty appliance and installs your replacement unit in a single coordinated visit so your daily routine is never interrupted.</li>
</ul>

<h2>4. Return at the End of Rental Tenure</h2>
<ul>
  <li>When your chosen rental period concludes, simply request a return through your account or by contacting our support desk.</li>
  <li>Our team will schedule a convenient pickup slot. Pickups are completely free of charge.</li>
  <li>Normal wear and tear resulting from everyday usage is fully expected and never penalized.</li>
</ul>

<h2>5. 100% Instant Deposit Refund on Return</h2>
<p>If you paid a refundable security deposit at the time of booking, it is refunded <strong>instantly on the spot via UPI or Cash</strong> as soon as our technician completes the pickup handover.</p>

<h2>6. Need to Return or Request a Repair?</h2>
<p>Our Bhopal & Sehore support desk is available 7 days a week:</p>
<ul>
  <li><strong>Phone:</strong> <a href="tel:+919300691533">+91 93006 91533</a></li>
  <li><strong>WhatsApp:</strong> <a href="https://wa.me/918770781490">+91 87707 81490</a></li>
  <li><strong>Email:</strong> <a href="mailto:rentlowin@gmail.com">rentlowin@gmail.com</a></li>
  <li><strong>Office Address:</strong> RentLow Technologies, MP Nagar Zone 2, Bhopal, Madhya Pradesh 462011</li>
</ul>`,
    updatedAt: "2026-10-23T00:00:00.000Z"
  }
];
